import { sync, get, call } from 'vuex-pathify';
import { Draw } from 'ol/interaction';
import { GeoJSON, Polyline } from 'ol/format';
import { Circle, Fill, Stroke, Style } from 'ol/style';

export default {
  computed: {
    isSketchActive: sync('tools/toolStatus@isSketchActive'),
    activeTool: get('tools/activeTool'),
    currentProjectId: get('layers/project.id'),
  },
  methods: {
    createProjectSketch: call('sketch/createProjectSketch'),
    getLineGeometry(features) {
      const geometry = features.map(feature => {
        const olGeometry = new Polyline().readGeometry(feature.geom, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        });
        const convertedGeometry = new GeoJSON().writeGeometryObject(olGeometry);
        return {
          type: 'Feature',
          geometry: convertedGeometry,
          properties: {
            id: feature.id,
            color: feature.color,
            stroke: feature.width,
            authorId: feature.added_by,
            authorName: feature.added_by_name,
            created: feature.added_at,
          },
        };
      });
      return {
        type: 'FeatureCollection',
        features: geometry ?? [],
        crs: {
          type: 'name',
          properties: {
            name: this.$_config.defaultEpsg,
          },
        },
      };
    },
    getSketchStyle(parameters) {
      const { color, stroke } = parameters;
      return new Style({
        stroke: new Stroke({
          color,
          width: stroke,
        }),
        image: new Circle({
          radius: stroke + 2,
          fill: new Fill({
            color,
          }),
        }),
      });
    },
    toggleSketchLayer(features) {
      if (!features) return this.addCustomGeojsonLayer({ id: 'sketches' });

      /** Since user can only draw with freehand mode for now, only polyline linestrings are supported  */
      const collection = this.getLineGeometry(features);

      this.addCustomGeojsonLayer({
        id: 'sketches',
        geometry: collection ?? undefined,
        layerParams: {
          renderImage: true,
          zIndex: 2000,
          style: ft => {
            const featureProperties = ft.getProperties();
            return new Style({
              stroke: new Stroke({
                color: featureProperties?.color ?? '#000',
                width: featureProperties?.stroke ?? 2,
              }),
            });
          },
        },
      });
    },
    changeSketchStyle(styleParameters) {
      const sketchInteraction = this.getInteractionByName('sketching');
      if (!sketchInteraction) return;
      this.turnOnSketching(styleParameters);
    },

    /** Sketch drawing interaction */
    async toggleSketching(value, styleParameters) {
      if (value && this.activeTool !== 'sketch') {
        this.$root.$emit('deactivateAllTools');
      } else if (!value && this.activeTool === 'sketch') {
        this.deactivateToolHandler('sketch');
      }
      await this.$nextTick();
      if (!value) return this.clearSketch();
      this.turnOnSketching(styleParameters);
      this.activeTool = 'sketch';
    },
    turnOnSketching(styleParameters) {
      this.turnOffSketching();
      const sketchingInteraction = new Draw({
        source: this.getLayerById('sketches')?.getSource() ?? undefined,
        type: 'LineString',
        freehand: true,
        style: this.getSketchStyle(styleParameters),
      });
      sketchingInteraction.set('name', 'sketching');
      this.map.addInteraction(sketchingInteraction);
      sketchingInteraction.on('drawend', $event => this.onSketchEnd($event, styleParameters));
    },
    turnOffSketching() {
      this.map.removeInteraction(this.getInteractionByName('sketching'));
    },
    clearSketch() {
      this.turnOffSketching();
    },
    async onSketchEnd(drawEvent, styleParameters) {
      const feature = drawEvent.feature;
      feature.setProperties(styleParameters);
      const featureObject = new GeoJSON().writeFeatureObject(feature);
      const featureGeojson = {
        ...featureObject,
        crs: {
          type: 'name',
          properties: {
            name: this.$_config.defaultEpsg,
          },
        },
      };
      try {
        const r = await this.createProjectSketch({
          projectId: this.currentProjectId,
          body: {
            color: styleParameters.color,
            width: styleParameters.stroke,
            feature: featureGeojson,
          },
        });
        this.toggleSketchLayer(r);
      } catch (error) {
        /** Remove drawn feature on error - it should be no longer on the map */
        this.getLayerByIdEmit({
          id: 'sketches',
          callback: layer => layer.getSource().removeFeature(drawEvent.feature),
        });
        console.error(error);
      }
    },

    /** IDENTIFICATION */
    async toggleSketchIdentification(value = !this.isSketchIdentificationActive, callback) {
      value ? this.$root.$emit('deactivateAllTools') : this.deactivateToolHandler('sketchIdentification');
      this.isSketchIdentificationActive = value;
      this.dettachCursorMoveHandler();
      await this.$nextTick();
      if (value) {
        this.isIdentificationToolActive = false;
        this.activeTool = 'sketchIdentification';
        this.attachListener(
          'click',
          this.getIdentificationCallback({
            identifyCallback: callback,
            specialType: 'sketch',
          }),
          {
            type: 'on',
            cursor: null,
          }
        );
        this.attachMoveCursorHandler('pointer', layer => layer.get('id') === 'sketches', true);
      }
    },
    identifySketchFeatureInfo(identifyCallback) {
      return e => {
        let detectedFeature;
        this.map.forEachFeatureAtPixel(
          e.pixel,
          feature => {
            detectedFeature = feature.getProperties();
            return true;
          },
          {
            hitTolerance: 5,
            layerFilter: layer => layer.get('id') === 'sketches',
          }
        );
        delete detectedFeature?.geometry;
        identifyCallback({
          coordinates: e.coordinate,
          feature: detectedFeature,
        });
      };
    },
  },
  mounted() {
    this.$root.$off('sketch-action');
    this.$root.$on('sketch-action', this.toggleSketching);

    this.$root.$off('sketchIdentification-action');
    this.$root.$on('sketchIdentification-action', this.toggleSketchIdentification);

    this.$root.$off('toggleSketchLayer');
    this.$root.$on('toggleSketchLayer', this.toggleSketchLayer);

    this.$root.$off('changeSketchStyle');
    this.$root.$on('changeSketchStyle', this.changeSketchStyle);
  },
};
